import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Clock, CheckCircle, X, Zap, AlertCircle, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const SevenMinutesLanding = () => {
  const [showStickyHeader, setShowStickyHeader] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyHeader(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Helmet>
        <title>Build Your Client Team in 7 Minutes | HireX8</title>
        <meta name="description" content="Tell us your project scope. Our AI matches you with pre-vetted talent in 7 minutes. Your team is ready before your next client call." />
        <meta property="og:title" content="Build Your Client Team in 7 Minutes | HireX8" />
        <meta property="og:description" content="Pre-vetted talent matched in 7 minutes. Zero platform fees. Built for digital agencies." />
      </Helmet>

      {/* Sticky Header CTA */}
      <div className={`fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm py-3 px-6 transition-all duration-300 ${showStickyHeader ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-white">HireX8™</Link>
          <a
            href="/meet-with-an-expert"
            className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-2 rounded-lg transition-all"
          >
            Talk to an Expert
          </a>
        </div>
      </div>

      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-orange-500">
        {/* Background Image */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/7minutes-hero.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-orange-600/95 via-orange-500/85 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full py-20">
          <div className="flex flex-col items-start text-left space-y-5 max-w-2xl">
            {/* Logo */}
            <Link to="/" className="mb-2">
              <span className="text-2xl font-bold text-white">HireX8™</span>
            </Link>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight">
              Build Your Client Team in <span className="text-black">7 Minutes.</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-white/90 max-w-xl leading-relaxed">
              Tell us your project scope. Our AI matches you with pre-vetted talent in 7 minutes. Your team is ready before your next client call.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col items-start gap-3 pt-2">
              <a
                href="/meet-with-an-expert"
                className="bg-black hover:bg-gray-900 text-white text-lg font-bold px-8 py-4 rounded-lg inline-flex items-center gap-3 transition-all shadow-lg"
              >
                Talk to an Expert
                <ArrowRight className="w-5 h-5" />
              </a>

              <p className="text-white/80 text-sm">
                Free 20-minute call. No commitment required.
              </p>
            </div>

            {/* Micro Bullets */}
            <div className="pt-4">
              <p className="text-white/70 text-sm font-medium uppercase tracking-wider mb-3">
                Instant Team Coverage For:
              </p>
              <div className="flex flex-wrap gap-3">
                {['Design', 'Copywriting', 'SEO', 'Engineering', 'Automation'].map((skill) => (
                  <span key={skill} className="bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — DIGITAL AGENCY PAIN */}
      <section className="py-24 px-6 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              Digital Agencies Don't Lose Clients Because of Talent.<br />
              <span className="text-orange-500">They Lose Because of Bottlenecks.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-12">
            {[
              'Your designer is underwater on three active projects',
              "A client wants to launch in two weeks — you have no one free",
              'You quoted a project and now can\'t staff it at margin',
              "You can't start for two weeks — client goes elsewhere",
              'Client wants AI capabilities you don\'t offer yet',
              'Freelancers keep dropping off mid-project',
            ].map((pain, index) => (
              <div key={index} className="flex items-center gap-3 bg-gray-800/50 p-4 rounded-lg">
                <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                <span className="text-gray-300">{pain}</span>
              </div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-2xl font-bold text-white mb-8">
              HireX8 eliminates the bottleneck in <span className="text-orange-500">7 minutes</span>.
            </p>
            <a
              href="/meet-with-an-expert"
              className="bg-orange-500 hover:bg-orange-600 text-white text-lg font-bold px-8 py-4 rounded-lg inline-flex items-center gap-3 transition-all"
            >
              Talk to an Expert
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 3 — HOW 7 MINUTES WORKS */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
              From Client Scope to Active Team in <span className="text-orange-500">3 Steps</span>.
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Define Scope</h3>
              <p className="text-gray-600 mb-4">Submit your client project:</p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-500" />
                  Deliverables
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-500" />
                  Timeline
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-500" />
                  Budget
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-500" />
                  Required roles
                </li>
              </ul>
              <p className="text-sm text-gray-500 mt-4 italic">(2–3 minute form)</p>
            </div>

            {/* Step 2 */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">AI Match Engine Activates</h3>
              <p className="text-gray-600">
                Our AI scores every operator on skill fit, past performance, project similarity, and budget alignment. Matches surface in 7 minutes. No browsing required.
              </p>
              <p className="text-sm text-gray-500 mt-4 italic">(Time: ~4 minutes)</p>
            </div>

            {/* Step 3 */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Team Ready</h3>
              <p className="text-gray-600 mb-4">You receive:</p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-500" />
                  No interviews or screening calls
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-500" />
                  Operators hit the ground running — zero onboarding lag
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-500" />
                  Fixed, transparent pricing — no platform fees
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-500" />
                  Delivery oversight included
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-500" />
                  Free replacement if a match underperforms
                </li>
              </ul>
              <p className="text-sm text-orange-600 mt-4 font-medium">Start immediately.</p>
            </div>
          </div>

          <div className="text-center mt-12">
            <a
              href="/meet-with-an-expert"
              className="bg-black hover:bg-gray-900 text-white text-lg font-bold px-8 py-4 rounded-lg inline-flex items-center gap-3 transition-all"
            >
              Talk to an Expert
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 4 — DIFFERENTIATION FROM UPWORK */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
              This Is No <span className="text-gray-400">"Freelance Marketplace"</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Left Column - Freelance Marketplace */}
            <div className="bg-white rounded-2xl p-8 border border-red-200">
              <h3 className="text-xl font-bold text-red-500 mb-6">Upwork / Freelance Marketplaces</h3>
              <ul className="space-y-4">
                {[
                  'Browse hundreds of profiles — find someone in days, not minutes',
                  'Pay 15–20% platform fee on every dollar',
                  'Review 50 proposals to find 3 worth interviewing',
                  'No quality guarantee — you absorb every missed deadline',
                  'You manage everything: onboarding, feedback, replacement',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-600">
                    <X className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Column - HireX8 */}
            <div className="bg-orange-50 rounded-2xl p-8 border border-orange-200">
              <h3 className="text-xl font-bold text-orange-500 mb-6">HireX8</h3>
              <ul className="space-y-4">
                {[
                  'AI matches your team in 7 minutes — no browsing required',
                  'Zero platform fees — your full budget goes to execution',
                  'Every operator pre-vetted and human-verified before you see them',
                  'Delivery guarantee — free replacement if needed, no questions asked',
                  'We manage oversight — you focus on the client relationship',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="text-center mt-12">
            <a
              href="/meet-with-an-expert"
              className="bg-black hover:bg-gray-900 text-white text-lg font-bold px-8 py-4 rounded-lg inline-flex items-center gap-3 transition-all"
            >
              Talk to an Expert
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 5 — AI POSITIONING */}
      <section className="py-24 px-6 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              The 7-Minute Match Isn't Magic.<br />
              <span className="text-orange-500">It's AI — Working the Way It Should.</span>
            </h2>
          </div>

          <div className="text-center mb-10">
            <p className="text-xl text-gray-300 mb-8">
              Our matching engine doesn't just find someone with the right title. It scores every operator on skill fit, past project performance, and how they work — including whether they use AI-assisted workflows that deliver faster output.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {[
                'Design production',
                'Content creation',
                'SEO automation',
                'Engineering acceleration',
                'Client reporting',
              ].map((skill) => (
                <span key={skill} className="bg-gray-800 text-white px-5 py-3 rounded-lg font-medium">
                  {skill}
                </span>
              ))}
            </div>
            <p className="text-lg text-gray-400 mb-2">
              You don't need to hire an AI team or build new systems. You need operators who already work with AI — and deliver faster because of it.
            </p>
            <p className="text-xl text-orange-500 font-medium mt-4">
              All delivered under your brand.
            </p>
          </div>

          <div className="text-center">
            <a
              href="/meet-with-an-expert"
              className="bg-orange-500 hover:bg-orange-600 text-white text-lg font-bold px-8 py-4 rounded-lg inline-flex items-center gap-3 transition-all"
            >
              Talk to an Expert
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 6 — BEFORE / AFTER 7 MINUTES */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
              What Changes in <span className="text-orange-500">7 Minutes</span>?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Before */}
            <div className="bg-gray-100 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-gray-500 mb-6 uppercase tracking-wider">Before</h3>
              <ul className="space-y-4">
                {[
                  'Slack thread asking who has capacity',
                  'Upwork search — 47 applicants, 3 worth reading',
                  'Back-and-forth emails negotiating scope and rate',
                  'Waiting two weeks for someone to start',
                  'Crossing your fingers the freelancer delivers',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-600 bg-white p-3 rounded-lg">
                    <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* After */}
            <div className="bg-orange-50 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-orange-500 mb-6 uppercase tracking-wider">After</h3>
              <ul className="space-y-4">
                {[
                  'Intake submitted — AI match running',
                  'Pre-vetted operator presented — no browsing required',
                  'Scope, rate, and timeline confirmed in one conversation',
                  'Operator starts this week — zero onboarding delay',
                  'Delivery guarantee in place — replacement free if needed',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-700 bg-white p-3 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-orange-500" />
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="text-center mt-12">
            <a
              href="/meet-with-an-expert"
              className="bg-black hover:bg-gray-900 text-white text-lg font-bold px-8 py-4 rounded-lg inline-flex items-center gap-3 transition-all"
            >
              Talk to an Expert
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 7 — SOCIAL PROOF */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Agency <span className="text-orange-500">Wins</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                ))}
              </div>
              <p className="text-lg text-gray-700 mb-6">
                "I submitted a scope on a Monday morning. By lunch I had two operators confirmed and ready to start. The client never knew we'd been at capacity. We delivered early."
              </p>
              <div>
                <p className="font-bold text-gray-900">Jamie L.</p>
                <p className="text-sm text-gray-500">Founder, 11-person digital agency · Seattle</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                ))}
              </div>
              <p className="text-lg text-gray-700 mb-6">
                "We were skeptical about the 7-minute claim. First match took nine minutes. We've been using HireX8 for three projects since. Average match time is now under six."
              </p>
              <div>
                <p className="font-bold text-gray-900">Priya N.</p>
                <p className="text-sm text-gray-500">Operations Director, growth agency · Toronto</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8 — FINAL CTA */}
      <section className="py-24 px-6 bg-orange-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Your Next Client Project Doesn't Need to Wait.
          </h2>

          <p className="text-xl text-white/90 mb-10">
            Talk to one of our agency specialists. Tell us what's on your plate and we'll show you exactly how fast we can match you — live, on the call.
          </p>

          <a
            href="/meet-with-an-expert"
            className="inline-flex items-center gap-3 bg-black hover:bg-gray-900 text-white text-xl font-bold px-12 py-5 rounded-lg shadow-lg transition-all hover:scale-105"
          >
            Book a Free 20-Min Call
            <ArrowRight className="w-6 h-6" />
          </a>

          <p className="mt-6 text-white/70 text-sm">
            No commitment. No sales pressure. Just a straight answer on fit.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-gray-900">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="text-2xl font-bold text-white">
            HireX8™
          </Link>
          <div className="flex items-center gap-6 text-gray-400 text-sm">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <span>© 2026 HireX8. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SevenMinutesLanding;
